import React, { FunctionComponent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { IconTextButton, QueryLoaderWrapper } from '@companion-professional/components';
import { Page } from '../../components/Page';
import { useCheckupRequest } from '../../hooks/useCheckupRequest';
import { PageNotFound } from '../PageNotFound';
import { CheckupDetails } from './CheckupDetails';
import { useMlForCheckup } from '../../hooks/useMlForCheckup';
import { ItemViewer } from './ItemViewer';
import { CheckupSurveys } from './CheckupSurveys';

type CheckupParams = {
  checkupId: string;
  petId?: string;
};

// Checkup is a page that shows the details of a checkup request.  This might include videos, images, and/or
// other data.  It also shows the surveys that are associated with the checkup.
export const Checkup: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { checkupId, petId } = useParams<CheckupParams>();
  if (!checkupId) {
    return <PageNotFound />;
  }

  const {
    isPending: isPendingCheckupReq,
    error: errorCheckupReq,
    checkupRequest: checkup
  } = useCheckupRequest(checkupId);
  const { isPending: isPendingMlReq, error: errorMlReg, mlForCheckup } = useMlForCheckup(checkupId);

  // Determine where to go back to based on whether we have a petId (in the params of the URL) or not.  If this page was
  // browsed to from a pet page, we want to go back to that pet page.  Otherwise, we want to go back to the dashboard.
  // In the future, it would be nice to have a breadcrumb trail that shows the path taken to get here, and then use that
  // to determine the return URL.
  const returnUrl = petId ? `/pets/${petId}` : '/';

  return (
    <Page title="Checkup" wrapWithSection={false}>
      <QueryLoaderWrapper
        isPending={isPendingCheckupReq || isPendingMlReq}
        error={errorCheckupReq || errorMlReg}
        errorHeading="Error loading checkup request"
      >
        {checkup ? (
          <div className="max-w-[1000px] space-y-4">
            <CheckupDetails checkup={checkup} returnUrl={returnUrl} />

            <ItemViewer checkup={checkup} mlForCheckup={mlForCheckup} />

            <CheckupSurveys checkup={checkup} />
          </div>
        ) : null}
      </QueryLoaderWrapper>

      <div className="mt-4">
        <IconTextButton
          size="small"
          variant="secondary"
          onClick={() => {
            // Only go back if there's navigation history, otherwise use returnUrl
            if (location.key !== 'default') {
              navigate(-1);
            } else {
              navigate(returnUrl);
            }
          }}
          Icon={ArrowLeft}
        >
          {petId
            ? !isPendingCheckupReq && checkup?.pet_name
              ? `Back To ${checkup.pet_name}`
              : 'Back To Pet'
            : 'Back To Dashboard'}
        </IconTextButton>
      </div>
    </Page>
  );
};
